"use client";

import { useState, useEffect } from "react";
import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEAL = "#4FCDC4";
const MK_BG = "#0f0f11";
const MK_CARD = "rgba(255,255,255,0.04)";
const MK_BD = "rgba(255,255,255,0.06)";
const MK_TEXT = "#a0a0a0";
const GREEN_OK = "#22c55e";

const HINT_TEXT = "Marketing leaders comparing Blaze CRM and Salesforce for a 50-person team";
const MATCH_CONVO = "I'm evaluating Blaze CRM vs Salesforce for our 50-person sales team. Which one scales better past Series B?";

export default function ChatGPTAdsContextHintsMockup() {
  const [ref, scrolled] = useScrollAnimate();
  const [hintTyped, setHintTyped] = useState("");
  const [arrowDrawn, setArrowDrawn] = useState(false);
  const [convoShown, setConvoShown] = useState(false);
  const [matchPill, setMatchPill] = useState(false);

  useEffect(() => {
    if (!scrolled) return;
    let typingInterval;
    const t0 = setTimeout(() => {
      let i = 0;
      typingInterval = setInterval(() => {
        i++;
        setHintTyped(HINT_TEXT.slice(0, i));
        if (i >= HINT_TEXT.length) clearInterval(typingInterval);
      }, 22);
    }, 300);
    const t1 = setTimeout(() => setArrowDrawn(true), 2200);
    const t2 = setTimeout(() => setConvoShown(true), 2700);
    const t3 = setTimeout(() => setMatchPill(true), 3200);
    return () => {
      clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (typingInterval) clearInterval(typingInterval);
    };
  }, [scrolled]);

  return (
    <div ref={ref} style={{
      margin: "24px 0",
      opacity: scrolled ? 1 : 0,
      transform: scrolled ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.4s ease, transform 0.4s ease",
      fontFamily: "var(--body)",
      maxWidth: 720, marginLeft: "auto", marginRight: "auto",
    }}>
      {/* Top: form portion with the Context hints field */}
      <div style={{
        background: MK_BG, borderRadius: 12,
        border: `1px solid ${MK_BD}`,
        padding: "20px 24px",
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 6 }}>
          Context hints
        </div>
        <div style={{ fontSize: 11, color: MK_TEXT, marginBottom: 10, lineHeight: 1.5 }}>
          Context hints provide more plain-language guidance on when your ad should appear. Describe the type of queries your ad will be most relevant to, or paste a set of keywords if you have them.
        </div>
        <div style={{
          background: MK_CARD,
          border: `1px solid ${hintTyped ? "rgba(79,205,196,0.4)" : MK_BD}`,
          borderRadius: 6, padding: "10px 12px",
          minHeight: 64, fontSize: 13, color: TEXT,
          fontFamily: "var(--body)",
          transition: "border-color 0.3s ease",
          lineHeight: 1.5,
        }}>
          {hintTyped || (
            <span style={{ color: MK_TEXT }}>Enter some context hints</span>
          )}
          {hintTyped && hintTyped.length < HINT_TEXT.length && (
            <span style={{
              display: "inline-block", width: 1, height: 14,
              background: TEAL, marginLeft: 1,
              animation: "blink 1s steps(2) infinite",
              verticalAlign: "middle",
            }} aria-hidden="true">|</span>
          )}
        </div>
        <div style={{
          display: "inline-block",
          background: "rgba(79,205,196,0.12)",
          border: "1px solid rgba(79,205,196,0.25)",
          borderRadius: 4, padding: "3px 10px",
          fontSize: 10, fontWeight: 600, color: TEAL,
          marginTop: 12,
          opacity: hintTyped ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}>
          Plain language. No keyword fields.
        </div>
      </div>

      {/* Diagram below */}
      <div style={{ marginTop: 28, position: "relative" }}>
        {/* Arrow down from form */}
        <div style={{
          width: 2, height: 24,
          background: arrowDrawn ? TEAL : "rgba(255,255,255,0.1)",
          margin: "0 auto",
          transition: "background 0.4s ease",
        }} />
        <div style={{
          textAlign: "center",
          fontSize: 18, color: arrowDrawn ? TEAL : "rgba(255,255,255,0.1)",
          marginTop: -8, marginBottom: 8,
          transition: "color 0.4s ease",
        }} aria-hidden="true">▼</div>

        {/* Embed node */}
        <div style={{
          margin: "0 auto", maxWidth: 280,
          background: MK_BG, borderRadius: 8,
          border: `1px solid ${arrowDrawn ? "rgba(79,205,196,0.4)" : MK_BD}`,
          padding: "10px 16px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          fontSize: 12, color: TEXT,
          transition: "border-color 0.4s ease",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span style={{ fontWeight: 600 }}>Embedding model</span>
        </div>

        {/* Arrow down to matched conversation */}
        <div style={{
          width: 2, height: 24,
          background: convoShown ? TEAL : "rgba(255,255,255,0.1)",
          margin: "8px auto 0",
          transition: "background 0.4s ease",
        }} />
        <div style={{
          textAlign: "center",
          fontSize: 18, color: convoShown ? TEAL : "rgba(255,255,255,0.1)",
          marginTop: -8, marginBottom: 8,
          transition: "color 0.4s ease",
        }} aria-hidden="true">▼</div>

        {/* Matched conversation card */}
        <div style={{
          background: MK_BG, borderRadius: 12,
          border: `1px solid ${convoShown ? "rgba(34,197,94,0.3)" : MK_BD}`,
          padding: "14px 18px",
          opacity: convoShown ? 1 : 0,
          transform: convoShown ? "translateY(0)" : "translateY(6px)",
          transition: "opacity 0.4s ease, transform 0.4s ease, border-color 0.4s ease",
          position: "relative",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: MK_TEXT,
            letterSpacing: "0.04em", marginBottom: 6, textTransform: "uppercase",
          }}>
            Live ChatGPT conversation
          </div>
          <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55 }}>
            "{MATCH_CONVO}"
          </div>
          {matchPill && (
            <div style={{
              position: "absolute", top: 14, right: 14,
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "rgba(34,197,94,0.15)",
              border: "1px solid rgba(34,197,94,0.35)",
              borderRadius: 999, padding: "3px 10px",
              fontSize: 10, fontWeight: 700, color: GREEN_OK,
              letterSpacing: "0.04em",
              opacity: matchPill ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              MATCH
            </div>
          )}
        </div>

        {/* AI Visibility Lift caption */}
        <div style={{
          textAlign: "center", fontSize: 12, color: MK_TEXT,
          marginTop: 18, lineHeight: 1.5, maxWidth: 520,
          margin: "18px auto 0",
          opacity: matchPill ? 1 : 0,
          transition: "opacity 0.4s ease 0.1s",
        }}>
          The same prompt research that earns organic AI citations is the same input that targets paid ads.
        </div>
      </div>
    </div>
  );
}
